import React, { useEffect, useState } from "react";
import { Card, CardBody, CardTitle, Col, Container, Form, FormGroup, Input, Label, Row, Button, Table, Collapse, Modal, ModalHeader, ModalBody, ModalFooter, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "rc-pagination";
import 'rc-pagination/assets/index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { getBranches } from "../../components/Api/GmsBranch";
import { getEmployees, getEmployeeByCode } from "../../components/Api/GmsEmployee";
import { getUsers, saveUser, checkUsernameExists } from "../../components/Api/GmsUser";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { FaEdit, FaEye, FaEyeSlash } from "react-icons/fa";
import PermissionManager from "../../components/Permission/PermissionManager";
import { getPages, saveUserPermissions } from "../../components/Api/GmsPermission";
import { getRoles, getRolePermissions } from "../../components/Api/GmsRole";
import { getSidebarName } from "../../components/Permission/PageNameMapper";
import { ClipLoader } from "react-spinners";

const DASHBOARD_PAGE_IDS = [9, 44, 45];
const SPA_EMPLOYEE_DASHBOARD_ID = 44;

// Add custom styles
const paginationStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '5px',
  '& .rc-pagination-item': {
    backgroundColor: '#fff',
    border: '1px solid #dee2e6',
    borderRadius: '50%',
    minWidth: '32px',
    height: '32px',
    lineHeight: '30px',
    textAlign: 'center',
    cursor: 'pointer',
    margin: '0 4px',
    '&:hover': {
      borderColor: '#556ee6',
      color: '#556ee6',
    },
    '&-active': {
      backgroundColor: '#556ee6',
      borderColor: '#556ee6',
      color: '#fff',
      '&:hover': {
        backgroundColor: '#485ec4',
      },
    },
  },
  '& .rc-pagination-prev, & .rc-pagination-next': {
    border: '1px solid #dee2e6',
    borderRadius: '50%',
    minWidth: '32px',
    height: '32px',
    lineHeight: '30px',
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': {
      borderColor: '#556ee6',
      color: '#556ee6',
    },
  },
};

// Form validation helper
const validateForm = (formData, isEdit, usernameExists, isViewOnlyRole, newUserPermissions, isSpaDashRole) => {
  const errors = {};

  // Role must be selected first
  if (!formData.user_Role_Id) {
    errors.user_Role_Id = 'Role is required';
  }

  if (!formData.user_UserName?.trim()) {
    errors.user_UserName = 'Username is required';
  } else if (usernameExists) {
    errors.user_UserName = 'Username already exists';
  }

  if (!isEdit && !formData.user_Password?.trim()) {
    errors.user_Password = 'Password is required';
  }

  // If role is view-only or Spa Employee Dashboard is selected, employee code is required
  const isSpaEmployeeDashboardInPermissions = newUserPermissions?.some(p => p.permission_Page_Id === SPA_EMPLOYEE_DASHBOARD_ID && p.permission_View);

  // Note: For existing users/roles, we'll need to check the role's permissions or the user's saved permissions.
  // For now, we focus on the UI requirement and mandated check.

  if ((isViewOnlyRole || isSpaEmployeeDashboardInPermissions || isSpaDashRole) && !formData.employee_Code) {
    errors.employee_Code = (isSpaEmployeeDashboardInPermissions || isSpaDashRole)
      ? 'Employee code is required for Spa Employee Dashboard access'
      : 'Employee code is required for view-only roles';
  }

  return errors;
};

const defaultForm = {
  user_Id: null,
  user_UserName: "",
  user_Password: "",
  user_Active: true,
  user_IsTempPwd: true,
  user_Branch_Id: null,
  user_EmployeeId: null,
  employee_Code: "",
  user_IsAdmin: false,
  user_Company_Id: null,
  user_Role_Id: null
};

const emptyFilter = {
  user_UserName: "",
  user_Active: null,
  user_Branch_Id: null,
  user_EmployeeId: null,
  user_Company_Id: null
};

const FormSelect = ({ label, id, name, options, value, onChange, error, required = true, disabled = false }) => (
  <div className="form-group">
    <Label className="col-form-label" htmlFor={id}>
      {label} {required && '*'}
    </Label>
    <Select
      id={id}
      name={name}
      options={options}
      value={value || null}
      onChange={onChange}
      className={error ? 'is-invalid' : ''}
      isClearable={!required}
      isDisabled={disabled}
    />
    {error && <div className="invalid-feedback d-block">{error}</div>}
  </div>
);

const FormInput = ({ label, id, name, type = "text", value, onChange, error, required = true, showPasswordToggle = false, onTogglePassword = null, showPassword = false }) => (
  <div className="form-group">
    <Label className="col-form-label" htmlFor={id}>
      {label} {required && '*'}
    </Label>
    <div className="input-group">
      <Input
        id={id}
        name={name}
        type={showPasswordToggle && showPassword ? "text" : type}
        value={value || ''}
        onChange={onChange}
        className={error ? 'is-invalid' : ''}
      />
      {showPasswordToggle && (
        <button
          type="button"
          className="btn btn-light border"
          onClick={onTogglePassword}
        >
          {showPassword ? <FaEyeSlash /> : <FaEye />}
        </button>
      )}
    </div>
    {error && <div className="invalid-feedback d-block">{error}</div>}
  </div>
);

export default function UserManagementPage() {
  const [formData, setFormData] = useState(defaultForm);
  const [filterData, setFilterData] = useState(emptyFilter);
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [allBranches, setAllBranches] = useState([]); // Store all branches for filtering
  const [filterBranches, setFilterBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filterCompanies, setFilterCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterEmployees, setFilterEmployees] = useState([]);
  const [employeeNames, setEmployeeNames] = useState({});
  const [fullEmployeesData, setFullEmployeesData] = useState([]);
  const [usernames, setUsernames] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(true);
  const itemsPerPage = 10;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUsername, setSelectedUsername] = useState("");
  const [showPermissions, setShowPermissions] = useState(false);
  const [availablePages, setAvailablePages] = useState([]);
  const [newUserPermissions, setNewUserPermissions] = useState([]);
  const [masterChecked, setMasterChecked] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameExists, setUsernameExists] = useState(false);
  const [roles, setRoles] = useState([]);
  const [isViewOnlyRole, setIsViewOnlyRole] = useState(false);
  const [isSpaEmployeeDashboardRole, setIsSpaEmployeeDashboardRole] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Clear username validation when switching between create/edit modes
  useEffect(() => {
    setUsernameExists(false);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.user_UserName;
      return newErrors;
    });
  }, [isEditing]);

  const fetchData = async () => {
    try {
      // Fetch all companies for the form
      const companiesResponse = await getBusinesses();
      setCompanies(companiesResponse.map(company => ({
        value: company.business_Id,
        label: company.business_NameEn
      })));

      // Load all branches for list display
      const branchesResponse = await getBranches();
      const branchOptions = branchesResponse.map(branch => ({
        value: branch.branch_Id,
        label: branch.branch_NameEn
      }));
      setBranches(branchOptions);
      setAllBranches(branchOptions); // Store all branches for filtering

      // Fetch all employees for the form and create name mapping
      const employeesResponse = await getEmployees();
      setFullEmployeesData(employeesResponse);
      setEmployees(employeesResponse.map(emp => ({
        value: emp.employee_Code,
        label: `${emp.employee_Code} - ${emp.employee_NameEn}`
      })));

      // Create employee name mapping using employee_Id as key
      const nameMap = {};
      employeesResponse.forEach(emp => {
        nameMap[emp.employee_Id] = emp.employee_NameEn;
      });
      setEmployeeNames(nameMap);

      // Load initial users
      await loadUsers();

      // Also fetch available pages
      const pagesData = await getPages();
      // Sort pages alphabetically by page_NameEn
      const sortedPages = pagesData.sort((a, b) => a.page_NameEn.localeCompare(b.page_NameEn));
      setAvailablePages(sortedPages);

      // Initialize default permissions (all false)
      setNewUserPermissions(sortedPages.map(page => ({
        permission_Page_Id: page.page_Id,
        permission_View: false,
        permission_Edit: false,
        permission_Delete: false,
        permission_Approve: false
      })));

      // Fetch roles
      const rolesData = await getRoles({ role_Active: true });
      setRoles(rolesData.map(role => ({
        value: role.role_Id,
        label: role.role_Name
      })));

    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load initial data",
        icon: "error"
      });
    }
  };

  // Load branches by company ID (for form)
  const loadBranchesByCompany = async (companyId) => {
    try {
      if (companyId) {
        const branchesResponse = await getBranches({ Branch_Buisness_Id: companyId });
        setBranches(branchesResponse.map(branch => ({
          value: branch.branch_Id,
          label: branch.branch_NameEn
        })));
      } else {
        setBranches([]);
      }
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  };

  // Load filter branches by company ID
  const loadFilterBranchesByCompany = async (companyId) => {
    try {
      if (companyId) {
        const branchesResponse = await getBranches({ Branch_Buisness_Id: companyId });
        const branchOptions = branchesResponse.map(branch => ({
          value: branch.branch_Id,
          label: branch.branch_NameEn
        }));
        // Filter to only show branches that are associated with users
        const userBranchIds = [...new Set(users.map(user => user.user_Branch_Id).filter(id => id != null))];
        const filteredOptions = branchOptions.filter(b => userBranchIds.includes(b.value));
        setFilterBranches(filteredOptions.sort((a, b) => a.label.localeCompare(b.label)));
      } else {
        setFilterBranches([]);
      }
    } catch (error) {
      console.error("Error loading filter branches:", error);
      setFilterBranches([]);
    }
  };

  // Handle company selection - load branches and clear branch selection
  const handleCompanySelect = async (option) => {
    const companyId = option ? option.value : null;

    // Update form data
    setFormData(prev => ({
      ...prev,
      user_Company_Id: companyId,
      user_Branch_Id: null // Clear branch selection when company changes
    }));

    // Load branches for selected company
    await loadBranchesByCompany(companyId);
  };

  // Username validation function
  const validateUsername = async (username) => {
    if (!username || !username.trim()) {
      setUsernameExists(false);
      return;
    }

    try {
      setIsCheckingUsername(true);
      const exists = await checkUsernameExists(username, formData.user_Id);
      setUsernameExists(exists);
    } catch (error) {
      console.error("Error validating username:", error);
      setUsernameExists(false);
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const loadUsers = async (filterData = emptyFilter) => {
    try {
      const data = await getUsers(filterData);
      setUsers(data);
      setTotalItems(data.length);

      // Update usernames list for filter dropdown
      const uniqueUsernames = [...new Set(data.map(user => user.user_UserName).filter(Boolean))];
      setUsernames(uniqueUsernames.map(username => ({
        value: username,
        label: username
      })));

      // Update filter employees list with only user-associated employees
      const userEmployeeIds = [...new Set(data.map(user => user.user_EmployeeId).filter(id => id != null))];

      // Fetch fresh employee data for the associated employees
      const employeesData = await getEmployees();

      // Update employee names mapping
      const nameMap = {};
      employeesData.forEach(emp => {
        nameMap[emp.employee_Id] = emp.employee_NameEn;
      });
      setEmployeeNames(nameMap);

      const employeeOptions = userEmployeeIds.map(id => {
        const employee = employeesData.find(e => e.employee_Id === id);
        return {
          value: id,
          label: employee ? `${employee.employee_Code} - ${employee.employee_NameEn}` : `Employee ${id}`
        };
      });
      setFilterEmployees(employeeOptions);

      // Update filter branches list - only if no company filter is selected
      // Otherwise, filter branches are loaded based on selected company
      if (!filterData.user_Company_Id) {
        const userBranchIds = [...new Set(data.map(user => user.user_Branch_Id).filter(id => id != null))];
        const branchOptions = userBranchIds.map(branchId => {
          const branch = allBranches.find(b => b.value === branchId);
          return branch || { value: branchId, label: `Branch ${branchId}` };
        }).sort((a, b) => a.label.localeCompare(b.label));
        setFilterBranches(branchOptions);
      } else {
        // If company filter is selected, reload branches for that company
        await loadFilterBranchesByCompany(filterData.user_Company_Id);
      }

      // Update filter companies list with only user-associated companies
      const userCompanyIds = [...new Set(data.map(user => user.user_Company_Id).filter(id => id != null))];
      const companyOptions = userCompanyIds.map(companyId => {
        const company = companies.find(c => c.value === companyId);
        return company || { value: companyId, label: `Company ${companyId}` };
      }).sort((a, b) => a.label.localeCompare(b.label));
      setFilterCompanies(companyOptions);

    } catch (error) {
      console.error("Error loading users:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to load users",
        icon: "error"
      });
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // If admin checkbox is checked, automatically set all permissions
    if (name === 'user_IsAdmin' && checked) {
      setMasterChecked(true);
      setShowPermissions(true); // Show permissions section so they get saved
      setNewUserPermissions(prev => prev.map(p => ({
        ...p,
        permission_View: true,
        permission_Edit: true,
        permission_Delete: true,
        permission_Approve: true
      })));
    }

    // If admin checkbox is unchecked, automatically uncheck all permissions
    if (name === 'user_IsAdmin' && !checked) {
      setMasterChecked(false);
      setNewUserPermissions(prev => prev.map(p => ({
        ...p,
        permission_View: false,
        permission_Edit: false,
        permission_Delete: false,
        permission_Approve: false
      })));
    }

    // Validate username when it changes
    if (name === 'user_UserName') {
      // Clear previous validation state
      setUsernameExists(false);
      // Debounce the validation to avoid too many API calls
      setTimeout(() => {
        validateUsername(value);
      }, 500);
    }
  };

  const handleFilterChange = async (selectedOption, field) => {
    const value = selectedOption?.value || '';

    // If company filter changes, reset branch filter and load branches for that company
    if (field === 'user_Company_Id') {
      setFilterData(prev => ({
        ...prev,
        [field]: value,
        user_Branch_Id: '' // Reset branch selection when company changes
      }));

      // Load branches for selected company
      if (value) {
        await loadFilterBranchesByCompany(value);
      } else {
        setFilterBranches([]);
      }
    } else {
      setFilterData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSelectFormChange = async (selectedOption, field) => {
    const value = selectedOption?.value ?? null;

    if (field.startsWith('filter_')) {
      const actualField = field.replace('filter_', '');
      setFilterData(prev => ({
        ...prev,
        [actualField]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));

      // If role is selected, check if it's view-only and load role permissions for initial permission popup
      if (field === 'user_Role_Id' && value) {
        await checkRoleViewOnlyStatus(value);
        // Load role permissions for initial permission defaults
        if (showPermissions) {
          await loadRolePermissionsForUser(value);
        }
      } else if (field === 'user_Role_Id' && !value) {
        setIsViewOnlyRole(false);
      }
    }
  };

  const checkRoleViewOnlyStatus = async (roleId) => {
    try {
      const rolePerms = await getRolePermissions(roleId);
      // Check if role has any Edit/Delete/Approve permissions
      const hasNonViewPermissions = rolePerms.some(p =>
        p.rolePermission_Edit ||
        p.rolePermission_Delete ||
        p.rolePermission_Approve > 0
      );
      setIsViewOnlyRole(!hasNonViewPermissions);

      // Check if role has Spa Employee Dashboard permission
      const hasSpaDashboard = rolePerms.some(p => p.rolePermission_Page_Id === SPA_EMPLOYEE_DASHBOARD_ID && p.rolePermission_View);
      setIsSpaEmployeeDashboardRole(hasSpaDashboard);

      if (hasSpaDashboard && !formData.employee_Code) {
        Swal.fire({
          title: "Employee Code Required",
          text: "The selected role includes permission for the 'Spa Employee Dashboard'. You must select an Employee Code for this user to proceed.",
          icon: "info"
        });
      }
    } catch (error) {
      console.error("Error checking role view-only status:", error);
      setIsViewOnlyRole(false);
      setIsSpaEmployeeDashboardRole(false);
    }
  };

  const loadRolePermissionsForUser = async (roleId) => {
    try {
      const rolePerms = await getRolePermissions(roleId);
      const permMap = {};
      rolePerms.forEach(p => {
        permMap[p.rolePermission_Page_Id] = p;
      });

      setNewUserPermissions(availablePages.map(page => {
        const existing = permMap[page.page_Id];
        return {
          permission_Page_Id: page.page_Id,
          permission_View: existing?.rolePermission_View || false,
          permission_Edit: existing?.rolePermission_Edit || false,
          permission_Delete: existing?.rolePermission_Delete || false,
          permission_Approve: existing?.rolePermission_Approve || 0
        };
      }));
    } catch (error) {
      console.error("Error loading role permissions:", error);
    }
  };

  const handlePermissionChange = (pageId, field, value) => {
    const isDashboardPage = DASHBOARD_PAGE_IDS.includes(pageId);
    const isGrantingPermission = field === 'permission_Approve' ? Number(value) > 0 : Boolean(value);

    if (pageId === SPA_EMPLOYEE_DASHBOARD_ID && isGrantingPermission && !formData.employee_Code) {
      Swal.fire({
        title: "Employee Code Required",
        text: "Please select an Employee Code from the user form first to enable Spa Employee Dashboard.",
        icon: "warning"
      });
    }

    setNewUserPermissions(prev => {
      let updated = prev.map(p =>
        p.permission_Page_Id === pageId
          ? {
            ...p,
            [field]: value,
            // Auto-select View when Edit is selected
            ...(field === 'permission_Edit' && value ? { permission_View: true } : {}),
            // Auto-select View and Edit when Delete is selected
            ...(field === 'permission_Delete' && value ? { permission_View: true, permission_Edit: true } : {}),
            // Auto-select all permissions when Approve is selected
            ...(field === 'permission_Approve' && value ? { permission_View: true, permission_Edit: true, permission_Delete: true } : {})
          }
          : p
      );

      // Mutually exclusive dashboards
      if (isDashboardPage && isGrantingPermission) {
        updated = updated.map(p => {
          if (DASHBOARD_PAGE_IDS.includes(p.permission_Page_Id) && p.permission_Page_Id !== pageId) {
            return {
              ...p,
              permission_View: false,
              permission_Edit: false,
              permission_Delete: false,
              permission_Approve: 0
            };
          }
          return p;
        });
      }

      // Update master checkbox state based on all permissions
      const allFullyChecked = updated.every(p =>
        p.permission_View && p.permission_Edit && p.permission_Delete && p.permission_Approve > 0
      );
      setMasterChecked(allFullyChecked);

      return updated;
    });
  };

  const handleMasterCheckboxChange = async (checked) => {
    if (checked) {
      // Identify dashboards in available pages
      const dashboardPages = availablePages.filter(p => DASHBOARD_PAGE_IDS.includes(p.page_Id));

      if (dashboardPages.length > 0) {
        const { value: selectedId, isDismissed } = await Swal.fire({
          title: 'Select Dashboard',
          text: 'Only one dashboard can be assigned to a user. Please choose one to include:',
          input: 'radio',
          inputOptions: dashboardPages.reduce((acc, p) => {
            acc[p.page_Id] = getSidebarName(p.page_Id, p.page_NameEn);
            return acc;
          }, { '0': 'None' }),
          inputValue: '0',
          showCancelButton: true,
          confirmButtonText: 'Allow All with Choice',
          cancelButtonText: 'Cancel'
        });

        if (isDismissed) return;

        setMasterChecked(true);
        setNewUserPermissions(prev => prev.map(p => {
          const isThisDashboard = DASHBOARD_PAGE_IDS.includes(p.permission_Page_Id);
          const isSelectedChoice = p.permission_Page_Id === parseInt(selectedId);

          const willGrant = !isThisDashboard || (isSelectedChoice && selectedId !== '0');

          return {
            ...p,
            permission_View: willGrant,
            permission_Edit: willGrant,
            permission_Delete: willGrant,
            permission_Approve: willGrant ? 3 : 0
          };
        }));
      } else {
        setMasterChecked(true);
        setNewUserPermissions(prev => prev.map(p => ({
          ...p,
          permission_View: true,
          permission_Edit: true,
          permission_Delete: true,
          permission_Approve: 3
        })));
      }
    } else {
      setMasterChecked(false);
      setNewUserPermissions(prev => prev.map(p => ({
        ...p,
        permission_View: false,
        permission_Edit: false,
        permission_Delete: false,
        permission_Approve: 0
      })));
    }
  };

  const handleRowLevelChange = (pageId, checked) => {
    const isDashboardPage = DASHBOARD_PAGE_IDS.includes(pageId);

    if (pageId === SPA_EMPLOYEE_DASHBOARD_ID && checked && !formData.employee_Code) {
      Swal.fire({
        title: "Employee Code Required",
        text: "Please select an Employee Code from the user form first to enable Spa Employee Dashboard.",
        icon: "warning"
      });
    }

    setNewUserPermissions(prev => {
      let updated = prev.map(p =>
        p.permission_Page_Id === pageId
          ? {
            ...p,
            permission_View: checked,
            permission_Edit: checked,
            permission_Delete: checked,
            permission_Approve: checked ? 3 : 0
          }
          : p
      );

      // Mutually exclusive dashboards
      if (isDashboardPage && checked) {
        updated = updated.map(p => {
          if (DASHBOARD_PAGE_IDS.includes(p.permission_Page_Id) && p.permission_Page_Id !== pageId) {
            return {
              ...p,
              permission_View: false,
              permission_Edit: false,
              permission_Delete: false,
              permission_Approve: 0
            };
          }
          return p;
        });
      }

      return updated;
    });
  };

  const isRowFullyChecked = (permission) => {
    return permission.permission_View &&
      permission.permission_Edit &&
      permission.permission_Delete &&
      permission.permission_Approve > 0;
  };

  // Check if any permissions are set for new user creation
  const hasPermissionsSet = () => {
    return newUserPermissions.some(p =>
      p.permission_View ||
      p.permission_Edit ||
      p.permission_Delete ||
      p.permission_Approve
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData, isEditing, usernameExists, isViewOnlyRole, newUserPermissions, isSpaEmployeeDashboardRole);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to top after React updates the DOM
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      return;
    }

    // Set loading state to prevent double submission
    setSaveLoading(true);

    try {
      // Create a new object for the request data
      const dataToSend = { ...formData };

      // If editing and password is empty or only whitespace, remove it completely from the request
      if (isEditing && (!dataToSend.user_Password || !dataToSend.user_Password.trim())) {
        delete dataToSend.user_Password;
      }

      const result = await saveUser(dataToSend);
      console.log('User save result:', result);

      if (result.success) {
        const savedUser = result.data;
        console.log('Saved user details:', savedUser);

        // If creating a new user and permissions are shown, save their permissions
        if (!isEditing && showPermissions) {
          if (!savedUser?.user_Id) {
            console.error('Cannot save permissions: No user ID returned from user creation');
            await Swal.fire({
              title: "Warning",
              text: "User was created but permissions could not be saved (No user ID returned)",
              icon: "warning"
            });
            setSaveLoading(false);
            return;
          } else {
            try {
              // Filter out permissions where nothing is granted
              const permissionsToSave = newUserPermissions.filter(p =>
                p.permission_View ||
                p.permission_Edit ||
                p.permission_Delete ||
                p.permission_Approve
              );

              console.log('Filtered permissions to save:', permissionsToSave);

              if (permissionsToSave.length > 0) {
                const permissionPayload = {
                  user_Id: savedUser.user_Id,
                  permissions: permissionsToSave.map(p => ({
                    permission_User_Id: savedUser.user_Id,
                    permission_Page_Id: p.permission_Page_Id,
                    permission_View: p.permission_View,
                    permission_Edit: p.permission_Edit,
                    permission_Delete: p.permission_Delete,
                    permission_Approve: p.permission_Approve
                  }))
                };

                console.log('Permission save payload:', permissionPayload);

                const permissionResult = await saveUserPermissions(permissionPayload);
                console.log('Permission save result:', permissionResult);

                if (!permissionResult.success) {
                  throw new Error(permissionResult.message);
                }
              } else {
                console.log('No permissions to save - all permissions are empty');
              }
            } catch (error) {
              console.error("Error saving initial permissions:", error);
              await Swal.fire({
                title: "Warning",
                text: `User was created but there was an error saving permissions: ${error.message || 'Unknown error'}`,
                icon: "warning"
              });
              setSaveLoading(false);
              return;
            }
          }
        }

        await Swal.fire({
          title: "Success!",
          text: isEditing ? "User updated successfully" : "User created successfully",
          icon: "success"
        });

        setFormData(defaultForm);
        setIsEditing(false);
        setShowPermissions(false);
        setMasterChecked(false);
        setIsViewOnlyRole(false);
        setNewUserPermissions(availablePages.map(page => ({
          permission_Page_Id: page.page_Id,
          permission_View: false,
          permission_Edit: false,
          permission_Delete: false,
          permission_Approve: false
        })));
        loadUsers(filterData);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Error saving user:", error);
      await Swal.fire({
        title: "Error!",
        text: error.message || "Failed to save user",
        icon: "error"
      });
    } finally {
      // Reset loading state after alert is shown
      setSaveLoading(false);
    }
  };

  const handleClearForm = () => {
    setFormData(defaultForm);
    setErrors({});
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    loadUsers(filterData);
  };

  const handleClearFilter = () => {
    setFilterData(emptyFilter);
    loadUsers(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const toggleFilter = () => {
    setIsFilterCollapsed(!isFilterCollapsed);
  };

  const handleEdit = async (user) => {
    // Find the employee by user_EmployeeId to get the employee_Code
    let employeeCode = "";
    if (user.user_EmployeeId) {
      const employee = fullEmployeesData.find(emp => emp.employee_Id === user.user_EmployeeId);
      if (employee) {
        employeeCode = employee.employee_Code;
      }
    }

    setFormData({
      ...user,
      user_Password: "", // Clear password field for security
      employee_Code: employeeCode // Set employee_Code for dropdown binding
    });
    setIsEditing(true);
    setActiveTab("1"); // Switch to Add/Edit tab

    // Load branches for the selected company if user has a company
    if (user.user_Company_Id) {
      await loadBranchesByCompany(user.user_Company_Id);
    }

    // Check if role is view-only or has Spa Employee Dashboard
    if (user.user_Role_Id) {
      await checkRoleViewOnlyStatus(user.user_Role_Id);
    } else {
      setIsViewOnlyRole(false);
      setIsSpaEmployeeDashboardRole(false);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdateUser = async () => {
    try {
      // Validate required fields
      if (!editingUser.user_UserName?.trim()) {
        Swal.fire({
          title: "Error!",
          text: "Username is required",
          icon: "error"
        });
        return;
      }

      const updateData = {
        ...editingUser
      };

      // If password is empty or only whitespace, remove it from the request
      if (!updateData.user_Password || !updateData.user_Password.trim()) {
        delete updateData.user_Password;
      }

      const result = await saveUser(updateData);

      if (result.success) {
        Swal.fire({
          title: "Success!",
          text: "User updated successfully",
          icon: "success"
        });

        setIsEditModalOpen(false);
        loadUsers(filterData);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Error updating user:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to update user",
        icon: "error"
      });
    }
  };

  const handleManagePermissions = (user) => {
    setSelectedUserId(user.user_Id);
    setSelectedUsername(user.user_UserName);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEmployeeSelect = async (selectedOption) => {
    try {
      if (!selectedOption) {
        // If no employee selected, clear employee and branch details
        setFormData(prev => ({
          ...prev,
          user_EmployeeId: null,
          user_Branch_Id: null,
          employee_Code: ""
        }));
        setEmployeeDetails(null);
        return;
      }

      // Get employee data from the existing employees list
      const employeeCode = selectedOption.value;

      // Find the employee from the already loaded full employees data
      const fullEmployeeData = fullEmployeesData.find(emp => emp.employee_Code === employeeCode);

      if (fullEmployeeData) {
        setEmployeeDetails({
          employee_Id: fullEmployeeData.employee_Id,
          employee_NameEn: fullEmployeeData.employee_NameEn,
          branch_Id: fullEmployeeData.employee_Branch_Id,
          branch_NameEn: branches.find(b => b.value === fullEmployeeData.employee_Branch_Id)?.label || 'Unknown Branch'
        });

        setFormData(prev => ({
          ...prev,
          user_EmployeeId: fullEmployeeData.employee_Id,
          user_Branch_Id: fullEmployeeData.employee_Branch_Id,
          employee_Code: employeeCode
        }));
      } else {
        // Handle case where employee is not found
        Swal.fire({
          title: "Warning",
          text: "Employee not found",
          icon: "warning"
        });
        setEmployeeDetails(null);
      }
    } catch (error) {
      console.error("Error fetching employee details:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to fetch employee details",
        icon: "warning"
      });
    }
  };

  return (
    <div className="page-content">
      <style>{`
        .user-table td.branch-cell,
        .user-table td.group-cell {
          white-space: normal !important;
          word-wrap: break-word !important;
        }
      `}</style>
      <Container fluid>
        <Breadcrumbs title="GMS" breadcrumbItem="User Management" />

        <Row>
          <Col lg="12">
            {selectedUserId ? (
              <>
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    color="secondary"
                    onClick={() => {
                      setSelectedUserId(null);
                      setSelectedUsername("");
                    }}
                  >
                    Back to User List
                  </Button>
                </div>
                <PermissionManager
                  userId={selectedUserId}
                  username={selectedUsername}
                />
              </>
            ) : (
              <>
                <Card>
                  <CardBody className="p-0">
                    <Nav tabs className="nav-tabs-custom">
                      <NavItem>
                        <NavLink
                          style={{
                            cursor: "pointer",
                            fontWeight: activeTab === "1" ? "bold" : "normal",
                            color: activeTab === "1" ? "#556ee6" : "#74788d",
                            borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                            padding: "15px 25px",
                            fontSize: "16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                          className={activeTab === "1" ? "active" : ""}
                          onClick={() => {
                            if (activeTab !== "1") {
                              setActiveTab("1");
                            }
                          }}
                        >
                          <i className="bx bx-plus-circle"></i>
                          <span>{isEditing ? 'Edit User' : 'Add User'}</span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          style={{
                            cursor: "pointer",
                            fontWeight: activeTab === "2" ? "bold" : "normal",
                            color: activeTab === "2" ? "#556ee6" : "#74788d",
                            borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                            padding: "15px 25px",
                            fontSize: "16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                          className={activeTab === "2" ? "active" : ""}
                          onClick={() => {
                            if (activeTab !== "2") {
                              setActiveTab("2");
                              loadUsers(filterData);
                            }
                          }}
                        >
                          <i className="bx bx-list-ul"></i>
                          <span>View Users</span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                    <TabContent activeTab={activeTab} className="p-4">
                      <TabPane tabId="1">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <CardTitle className="mb-0">{isEditing ? 'Edit User' : 'Create New User'}</CardTitle>
                          {!isEditing && (
                            <Button
                              color="primary"
                              onClick={async () => {
                                const newShowPermissions = !showPermissions;
                                setShowPermissions(newShowPermissions);
                                // If showing permissions and a role is selected, load role permissions
                                if (newShowPermissions && formData.user_Role_Id) {
                                  await loadRolePermissionsForUser(formData.user_Role_Id);
                                }
                              }}
                              className="btn-sm"
                            >
                              {showPermissions ? 'Hide Permissions' : 'Set Initial Permissions'}
                            </Button>
                          )}
                        </div>

                        <Form onSubmit={handleSubmit}>
                          <Row className="mb-3">
                            <Col md={4}>
                              <FormSelect
                                label="Role *"
                                id="user_Role_Id"
                                name="user_Role_Id"
                                options={roles}
                                value={roles.find(r => r.value === formData.user_Role_Id) || null}
                                onChange={(option) => handleSelectFormChange(option, 'user_Role_Id')}
                                error={errors.user_Role_Id}
                                required={true}
                                isClearable={false}
                                placeholder="Select role first"
                              />
                            </Col>
                            <Col md={4}>
                              <FormSelect
                                label="Company"
                                id="user_Company_Id"
                                name="user_Company_Id"
                                options={companies}
                                value={companies.find(c => c.value === formData.user_Company_Id) || null}
                                onChange={handleCompanySelect}
                                error={errors.user_Company_Id}
                                required={false}
                                isClearable={true}
                                placeholder="Select company"
                              />
                            </Col>
                            <Col md={4}>
                              <FormSelect
                                label="Branch"
                                id="user_Branch_Id"
                                name="user_Branch_Id"
                                options={branches}
                                value={branches.find(b => b.value === formData.user_Branch_Id) || null}
                                onChange={(option) => handleSelectFormChange(option, 'user_Branch_Id')}
                                error={errors.user_Branch_Id}
                                required={false}
                                isClearable={true}
                                placeholder="Select branch"
                              />
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col md={6}>
                              <FormInput
                                label="Username *"
                                id="user_UserName"
                                name="user_UserName"
                                value={formData.user_UserName}
                                onChange={handleFormChange}
                                error={errors.user_UserName || (usernameExists && !isEditing ? 'Username already exists' : null)}
                                disabled={!formData.user_Role_Id}
                              />
                              {!formData.user_Role_Id && (
                                <small className="text-muted">Please select a role first</small>
                              )}
                              {isCheckingUsername && formData.user_Role_Id && (
                                <small className="text-muted">Checking username...</small>
                              )}
                              {!isCheckingUsername && formData.user_UserName && !usernameExists && !errors.user_UserName && formData.user_Role_Id && (
                                <small className="text-success">Username is available</small>
                              )}
                            </Col>
                            <Col md={6}>
                              <FormInput
                                label={isEditing ? "New Password (leave blank to keep current)" : "Password *"}
                                id="user_Password"
                                name="user_Password"
                                type="password"
                                value={formData.user_Password}
                                onChange={handleFormChange}
                                error={errors.user_Password}
                                required={!isEditing}
                                showPasswordToggle={true}
                                onTogglePassword={() => setShowPassword(!showPassword)}
                                showPassword={showPassword}
                                disabled={!formData.user_Role_Id}
                              />
                              {!formData.user_Role_Id && (
                                <small className="text-muted">Please select a role first</small>
                              )}
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            {((isViewOnlyRole || isSpaEmployeeDashboardRole || newUserPermissions.find(p => p.permission_Page_Id === SPA_EMPLOYEE_DASHBOARD_ID)?.permission_View)) && formData.user_Role_Id && (
                              <Col md={6}>
                                <FormSelect
                                  label="Employee Code *"
                                  id="employee_Code"
                                  name="employee_Code"
                                  options={employees}
                                  value={employees.find(e => e.value === formData.employee_Code) || null}
                                  onChange={handleEmployeeSelect}
                                  error={errors.employee_Code}
                                  required={true}
                                  isClearable={false}
                                  isDisabled={isEditing && formData.user_EmployeeId} // Only disable if already set during edit
                                  placeholder="Select employee code"
                                />
                              </Col>
                            )}
                          </Row>



                          <Row className="mb-4">
                            <Col md={4}>
                              <FormGroup check className="mb-3">
                                <Input
                                  type="checkbox"
                                  id="user_Active"
                                  name="user_Active"
                                  checked={formData.user_Active}
                                  onChange={handleFormChange}
                                />
                                <Label check>Active</Label>
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup check className="mb-3">
                                <Input
                                  type="checkbox"
                                  id="user_IsTempPwd"
                                  name="user_IsTempPwd"
                                  checked={formData.user_IsTempPwd}
                                  onChange={handleFormChange}
                                />
                                <Label check>Temporary Password</Label>
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup check className="mb-3">
                                <Input
                                  type="checkbox"
                                  id="user_IsAdmin"
                                  name="user_IsAdmin"
                                  checked={formData.user_IsAdmin}
                                  onChange={handleFormChange}
                                />
                                <Label check>Admin User</Label>
                              </FormGroup>
                            </Col>
                          </Row>

                          {!isEditing && showPermissions && (
                            <div className="mt-4">
                              <h5>Initial Permissions</h5>
                              <div className="table-responsive">
                                <Table className="table-bordered">
                                  <thead>
                                    <tr>
                                      <th>Page</th>
                                      <th className="text-center" style={{ width: '100px' }}>
                                        <div>All</div>
                                        <input
                                          type="checkbox"
                                          checked={masterChecked}
                                          onChange={(e) => handleMasterCheckboxChange(e.target.checked)}
                                          className="master-checkbox"
                                        />
                                      </th>
                                      <th className="text-center" style={{ width: '100px' }}>View</th>
                                      <th className="text-center" style={{ width: '100px' }}>Edit</th>
                                      <th className="text-center" style={{ width: '100px' }}>Delete</th>
                                      <th className="text-center" style={{ width: '100px' }}>Approve</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {availablePages.map(page => {
                                      const currentPermission = newUserPermissions.find(p => p.permission_Page_Id === page.page_Id);
                                      // Use sidebar name if available, otherwise fall back to database name
                                      const displayName = getSidebarName(page.page_Id, page.page_NameEn);
                                      return (
                                        <tr key={page.page_Id}>
                                          <td>{displayName}</td>
                                          <td className="text-center">
                                            <input
                                              type="checkbox"
                                              checked={isRowFullyChecked(currentPermission)}
                                              onChange={(e) => handleRowLevelChange(page.page_Id, e.target.checked)}
                                              className="row-master-checkbox"
                                            />
                                          </td>
                                          <td className="text-center">
                                            <input
                                              type="checkbox"
                                              checked={currentPermission?.permission_View || false}
                                              onChange={(e) => handlePermissionChange(page.page_Id, 'permission_View', e.target.checked)}
                                            />
                                          </td>
                                          <td className="text-center">
                                            <input
                                              type="checkbox"
                                              checked={currentPermission?.permission_Edit || false}
                                              onChange={(e) => handlePermissionChange(page.page_Id, 'permission_Edit', e.target.checked)}
                                            />
                                          </td>
                                          <td className="text-center">
                                            <input
                                              type="checkbox"
                                              checked={currentPermission?.permission_Delete || false}
                                              onChange={(e) => handlePermissionChange(page.page_Id, 'permission_Delete', e.target.checked)}
                                            />
                                          </td>
                                          <td className="text-center">
                                            <input
                                              type="checkbox"
                                              checked={currentPermission?.permission_Approve || false}
                                              onChange={(e) => handlePermissionChange(page.page_Id, 'permission_Approve', e.target.checked)}
                                            />
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                            </div>
                          )}

                          <div className="d-flex justify-content-end mt-3">
                            <Button
                              type="button"
                              color="secondary"
                              className="me-2"
                              onClick={handleClearForm}
                              disabled={saveLoading}
                            >
                              Clear
                            </Button>
                            {isEditing ? (
                              <Button type="submit" color="primary" disabled={saveLoading}>
                                {saveLoading ? (
                                  <>
                                    <ClipLoader size={16} color="#ffffff" className="me-2" />
                                    Updating...
                                  </>
                                ) : (
                                  "Update"
                                )}
                              </Button>
                            ) : (
                              hasPermissionsSet() && (
                                <Button type="submit" color="primary" disabled={saveLoading}>
                                  {saveLoading ? (
                                    <>
                                      <ClipLoader size={16} color="#ffffff" className="me-2" />
                                      Saving...
                                    </>
                                  ) : (
                                    "Save"
                                  )}
                                </Button>
                              )
                            )}
                          </div>
                        </Form>
                      </TabPane>
                      <TabPane tabId="2">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <CardTitle className="h4 mb-0">Filter Users</CardTitle>
                          <Button
                            type="button"
                            color="light"
                            onClick={toggleFilter}
                            className="btn-sm"
                          >
                            {isFilterCollapsed ? 'Show Filters' : 'Hide Filters'}
                          </Button>
                        </div>

                        <Collapse isOpen={!isFilterCollapsed}>
                          <Row>
                            <Col md={3}>
                              <FormGroup>
                                <Label>Username</Label>
                                <Select
                                  id="filter_username"
                                  name="user_UserName"
                                  options={[{ value: '', label: 'All' }, ...usernames]}
                                  value={filterData.user_UserName ? { value: filterData.user_UserName, label: filterData.user_UserName } : { value: '', label: 'All' }}
                                  onChange={(option) => handleFilterChange(option, 'user_UserName')}
                                  isClearable
                                  isSearchable
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label>Branch</Label>
                                <Select
                                  id="filter_branch"
                                  name="user_Branch_Id"
                                  options={[{ value: '', label: 'All' }, ...filterBranches]}
                                  value={
                                    filterData.user_Branch_Id
                                      ? filterBranches.find(b => b.value === filterData.user_Branch_Id) || { value: '', label: 'All' }
                                      : { value: '', label: 'All' }
                                  }
                                  onChange={(option) => handleFilterChange(option, 'user_Branch_Id')}
                                  isClearable
                                  isSearchable
                                  isDisabled={!filterData.user_Company_Id}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label>Group</Label>
                                <Select
                                  id="filter_company"
                                  name="user_Company_Id"
                                  options={[{ value: '', label: 'All' }, ...filterCompanies]}
                                  value={
                                    filterData.user_Company_Id
                                      ? filterCompanies.find(c => c.value === filterData.user_Company_Id) || { value: '', label: 'All' }
                                      : { value: '', label: 'All' }
                                  }
                                  onChange={(option) => handleFilterChange(option, 'user_Company_Id')}
                                  isClearable
                                  isSearchable
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label>Employee ID</Label>
                                <Select
                                  id="filter_employee"
                                  name="user_EmployeeId"
                                  options={[{ value: '', label: 'All' }, ...filterEmployees]}
                                  value={filterData.user_EmployeeId ? filterEmployees.find(e => e.value === filterData.user_EmployeeId) : { value: '', label: 'All' }}
                                  onChange={(option) => handleFilterChange(option, 'user_EmployeeId')}
                                  isClearable
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label>Status</Label>
                                <Select
                                  id="filter_active"
                                  name="user_Active"
                                  options={[
                                    { value: '', label: 'All' },
                                    { value: true, label: 'Active' },
                                    { value: false, label: 'Inactive' }
                                  ]}
                                  value={
                                    filterData.user_Active === null
                                      ? { value: '', label: 'All' }
                                      : { value: filterData.user_Active, label: filterData.user_Active ? 'Active' : 'Inactive' }
                                  }
                                  onChange={(option) => handleFilterChange(option, 'user_Active')}
                                  isClearable={false}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <div className="d-flex justify-content-end mt-3">
                            <Button
                              type="button"
                              color="secondary"
                              className="me-2"
                              onClick={handleClearFilter}
                            >
                              Clear
                            </Button>
                            <Button
                              type="button"
                              color="primary"
                              onClick={handleApplyFilter}
                            >
                              Apply Filter
                            </Button>
                          </div>
                        </Collapse>

                        <Card className="mt-4">
                          <CardBody>
                            <CardTitle className="mb-4">User List</CardTitle>
                            <div className="table-responsive" style={{ overflowX: 'auto' }}>
                              <Table className="table-centered table-nowrap mb-0 user-table">
                                <thead>
                                  <tr>
                                    <th style={{ width: '40px' }}>#</th>
                                    <th style={{ minWidth: '100px' }}>Username</th>
                                    <th style={{ width: '100px' }}>Employee Code</th>
                                    <th style={{ minWidth: '150px' }}>Employee Name</th>
                                    <th style={{ maxWidth: '150px', width: '150px' }}>Branch</th>
                                    <th style={{ maxWidth: '150px', width: '150px' }}>Group</th>
                                    <th style={{ width: '80px' }}>Status</th>
                                    <th style={{ width: '70px' }}>Admin</th>
                                    <th style={{ minWidth: '180px', whiteSpace: 'nowrap', width: '180px' }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {users
                                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                    .map((user, index) => (
                                      <tr key={`user-${user.user_Id || index}`}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>{user.user_UserName}</td>
                                        <td>{fullEmployeesData.find(emp => emp.employee_Id === user.user_EmployeeId)?.employee_Code || '-'}</td>
                                        <td>{employeeNames[user.user_EmployeeId] || '-'}</td>
                                        <td className="branch-cell" style={{ maxWidth: '150px', width: '150px', lineHeight: '1.4', padding: '8px' }}>
                                          <div style={{
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            whiteSpace: 'normal',
                                            lineHeight: '1.4em',
                                            maxHeight: '2.8em',
                                            overflow: 'hidden'
                                          }}>
                                            {user.user_Branch_Id ? branches.find(b => b.value === user.user_Branch_Id)?.label : '-'}
                                          </div>
                                        </td>
                                        <td className="group-cell" style={{ maxWidth: '150px', width: '150px', lineHeight: '1.4', padding: '8px' }}>
                                          <div style={{
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            whiteSpace: 'normal',
                                            lineHeight: '1.4em',
                                            maxHeight: '2.8em',
                                            overflow: 'hidden'
                                          }}>
                                            {user.user_Company_Id ? companies.find(c => c.value === user.user_Company_Id)?.label : '-'}
                                          </div>
                                        </td>
                                        <td>
                                          <span className={`badge ${user.user_Active ? 'bg-success' : 'bg-danger'}`}>
                                            {user.user_Active ? 'Active' : 'Inactive'}
                                          </span>
                                        </td>
                                        <td>
                                          <span className={`badge ${user.user_IsAdmin ? 'bg-primary' : 'bg-secondary'}`}>
                                            {user.user_IsAdmin ? 'Yes' : 'No'}
                                          </span>
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                          <div className="d-flex gap-1" style={{ flexWrap: 'nowrap' }}>
                                            <Button
                                              color="primary"
                                              size="sm"
                                              onClick={() => handleEdit(user)}
                                              className="btn-sm"
                                              style={{ fontSize: '11px', padding: '3px 6px', whiteSpace: 'nowrap' }}
                                              title="Edit User"
                                            >
                                              <FaEdit style={{ marginRight: '3px', fontSize: '11px' }} />
                                              Edit
                                            </Button>
                                            <Button
                                              color="info"
                                              size="sm"
                                              onClick={() => handleManagePermissions(user)}
                                              className="btn-sm"
                                              style={{ fontSize: '11px', padding: '3px 6px', whiteSpace: 'nowrap' }}
                                              title="Manage Permissions"
                                            >
                                              Permissions
                                            </Button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </Table>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-3">
                              <div className="text-muted">
                                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{" "}
                                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                              </div>
                              <div className="pagination-wrapper">
                                <Pagination
                                  className="ant-pagination"
                                  current={currentPage}
                                  total={totalItems}
                                  pageSize={itemsPerPage}
                                  onChange={handlePageChange}
                                  showTitle={false}
                                />
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </TabPane>
                    </TabContent>
                  </CardBody>
                </Card>
              </>
            )}
          </Col>
        </Row>
      </Container>

      {/* Edit User Modal */}
      <Modal isOpen={isEditModalOpen} toggle={() => setIsEditModalOpen(!isEditModalOpen)} size="lg">
        <ModalHeader toggle={() => setIsEditModalOpen(!isEditModalOpen)}>
          Edit User: {editingUser?.user_UserName}
        </ModalHeader>
        <ModalBody>
          {editingUser && (
            <Form>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Username</Label>
                    <Input
                      type="text"
                      value={editingUser.user_UserName}
                      onChange={(e) => setEditingUser({
                        ...editingUser,
                        user_UserName: e.target.value
                      })}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>New Password (leave blank to keep current)</Label>
                    <Input
                      type="password"
                      value={editingUser.user_Password}
                      onChange={(e) => setEditingUser({
                        ...editingUser,
                        user_Password: e.target.value
                      })}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <FormGroup check className="mb-3">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={editingUser.user_Active}
                        onChange={(e) => setEditingUser({
                          ...editingUser,
                          user_Active: e.target.checked
                        })}
                      />{' '}
                      Active
                    </Label>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup check className="mb-3">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={editingUser.user_IsTempPwd}
                        onChange={(e) => setEditingUser({
                          ...editingUser,
                          user_IsTempPwd: e.target.checked
                        })}
                      />{' '}
                      Temporary Password
                    </Label>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup check className="mb-3">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={editingUser.user_IsAdmin}
                        onChange={(e) => setEditingUser({
                          ...editingUser,
                          user_IsAdmin: e.target.checked
                        })}
                      />{' '}
                      Admin User
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Employee ID</Label>
                    <Input
                      type="text"
                      value={editingUser.user_EmployeeId}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Branch</Label>
                    <Input
                      type="text"
                      value={branches.find(b => b.value === editingUser.user_Branch_Id)?.label || ''}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsEditModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleUpdateUser}>
            Update User
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
} 